import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Peer, Peers } from '../../../types/SignalR';
import { Button } from '../../FormComponents/Buttons/Button';
import TranslationHtml from '../../TranslationHtml/TranslationHtml';

import './ProjectUserFooter.scss';
import { UserTypeEnum } from '../../../types/enums/UserTypesEnum';
import useProject from '../../../hooks/useProject';

type ProjectUserFooter = {
  token?: string;
  sendRequestControlCallback: () => void;
  sendTakeControlCallback: () => void;
  sendGiveControlCallback: () => void;
  getPeerInfoCallback: (peerId: string) => Peer | null;
  peerRequestingControl: Peer | null;
  connectedPeers: Peers;
};

const ProjectUserFooter = ({
  token,
  sendRequestControlCallback,
  sendTakeControlCallback,
  sendGiveControlCallback,
  getPeerInfoCallback,
  peerRequestingControl,
  connectedPeers,
}: ProjectUserFooter) => {
  const [requestedControl, setRequestedControl] = useState<boolean>(false);
  const { t } = useTranslation();
  const { project } = useProject();

  const handleRequestControl = () => {
    setRequestedControl(true);
    sendRequestControlCallback();
  };

  const handleTakeControl = () => {
    setRequestedControl(false);
    sendTakeControlCallback();
  };

  const handleGiveControl = () => {
    setRequestedControl(false);
    sendGiveControlCallback();
  };

  useEffect(() => {
    setRequestedControl(false);
  }, [connectedPeers]);

  if (project?.broadcastEndedUtc || project?.deletedUtc) {
    return null;
  }

  return (
    <div className={(peerRequestingControl ? 'active ' : '') + 'main-section-footer level'}>
      <div className="columns">
        <div className="column is-full control-switcher">
          {getPeerInfoCallback(token ?? '')?.isCurrentlyLive && !peerRequestingControl ? (
            <>
              <p className="is-currently-live-text">
                {getPeerInfoCallback(token ?? '')?.userType === UserTypeEnum.Captioner
                  ? t('labels.is-currently-live-captioner')
                  : t('labels.is-currently-live-signer')}
              </p>
            </>
          ) : null}
          {!getPeerInfoCallback(token ?? '')?.isCurrentlyLive ? (
            <Button
              translationKey="button.request-control"
              variant="secondary"
              color="blue"
              additionalClassName="btn-no-margin"
              onClick={handleRequestControl}
              type="button"
              disabled={requestedControl}
            />
          ) : null}{' '}
          {requestedControl && (
            <>
              <Button
                translationKey="button.take-control"
                variant="primary"
                color="pink"
                additionalClassName="btn-no-margin"
                onClick={handleTakeControl}
                type="button"
              />
              <p className="take-control-notice">{t('project-user.footer.take-control-notice')}</p>
            </>
          )}
          {peerRequestingControl ? (
            <>
              <TranslationHtml
                htmlString={t('project-user.footer.user-requested-control', {
                  user: peerRequestingControl?.userName,
                })}
                className={'user-requested'}
              />
              <Button
                translationKey="button.give-control"
                variant="secondary"
                color="blue"
                additionalClassName="btn-no-margin"
                onClick={handleGiveControl}
                type="button"
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProjectUserFooter;
